import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // De client heeft detectSessionInUrl uitgeschakeld, dus de sessie uit de resetlink
  // (#access_token=... of ?code=...) moet hier zelf worden opgezet.
  const sessionReady = useRef(null);
  if (sessionReady.current === null) sessionReady.current = Promise.resolve();

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const query = new URLSearchParams(window.location.search);

    if (hash.get("error") || query.get("error")) {
      setError("Deze resetlink is verlopen of al gebruikt. Vraag een nieuwe reset link aan.");
      return;
    }

    const accessToken = hash.get("access_token");
    const refreshToken = hash.get("refresh_token");
    const code = query.get("code");

    if (accessToken && refreshToken) {
      sessionReady.current = supabase.auth
        .setSession({ access_token: accessToken, refresh_token: refreshToken })
        .then(({ error: sessionError }) => {
          if (sessionError) setError("Sessie verlopen. Vraag een nieuwe reset link aan.");
        });
    } else if (code) {
      sessionReady.current = supabase.auth.exchangeCodeForSession(code).then(({ error: sessionError }) => {
        if (sessionError) setError("Sessie verlopen. Vraag een nieuwe reset link aan.");
      });
    } else {
      return;
    }
    window.history.replaceState(null, "", window.location.pathname);
  }, []);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");

    console.log("[ResetPassword] Starting password reset...");

    // Validate passwords
    if (password.length < 6) {
      setError("Wachtwoord moet minstens 6 karakters bevatten.");
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Wachtwoorden komen niet overeen.");
      setLoading(false);
      return;
    }

    try {
      // Wacht tot de sessie uit de link is opgezet
      await sessionReady.current;
      const { data: { session } } = await supabase.auth.getSession();
      console.log("[ResetPassword] Current session:", session ? "exists" : "null");

      if (!session) {
        setError("Sessie verlopen. Vraag een nieuwe reset link aan.");
        setLoading(false);
        return;
      }

      console.log("[ResetPassword] Calling updateUser...");
      const { data, error } = await supabase.auth.updateUser({
        password: password,
      });

      console.log("[ResetPassword] updateUser response:", { data, error });

      if (error) {
        console.error("[ResetPassword] Error:", error);
        setError(error.message || "Er is een fout opgetreden bij het instellen van het wachtwoord.");
        setLoading(false);
      } else {
        console.log("[ResetPassword] Success!");
        setMessage("Wachtwoord succesvol aangepast! Je wordt doorgestuurd naar login...");
        setLoading(false);
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      }
    } catch (err) {
      console.error("[ResetPassword] Unexpected error:", err);
      setError("Er is een onverwachte fout opgetreden: " + err.message);
      setLoading(false);
    }
  };

  // Show password reset form directly - session will be validated on submit
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md border border-gray-200">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="mb-4">
            <h1 className="text-3xl font-bold text-gray-800 mb-1">
              Dr. K. Hellinckx BV
            </h1>
            <div className="text-sm text-gray-500">Voorraad Beheer Systeem</div>
          </div>
          <p className="text-gray-600 text-sm font-medium">
            Stel je nieuwe wachtwoord in
          </p>
        </div>

        {/* Success message */}
        {message && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
            {message}
          </div>
        )}

        {/* Password Reset Form */}
        <form onSubmit={handlePasswordReset}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nieuw wachtwoord
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="••••••••"
              required
              minLength={6}
              disabled={loading}
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bevestig wachtwoord
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="••••••••"
              required
              minLength={6}
              disabled={loading}
            />
          </div>

          {/* Error message for form validation */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Bezig..." : "Wachtwoord instellen"}
          </button>
        </form>

        {/* Back to login */}
        <div className="mt-4 text-center text-sm">
          <p className="text-gray-600">
            Terug naar{" "}
            <button
              onClick={() => navigate("/login")}
              className="text-blue-600 hover:text-blue-700 font-semibold"
            >
              inloggen
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
